import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Building2, MapPin, Phone, Mail, Globe, Truck, ChevronRight, Check, TrainFront, Ship } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const CompanySetupModal = ({ isOpen, onComplete, onClose }) => {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoGenerating, setLogoGenerating] = useState(false);
  
  // Company info
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    deletion_pin: ''
  });
  
  // Terminal selection
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [includeRailRamps, setIncludeRailRamps] = useState(false);
  
  const [generatedLogo, setGeneratedLogo] = useState(null);

  // Fetch regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await api.get('/terminals/regions');
        setRegions(response.data.regions);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };
    if (isOpen) fetchRegions();
  }, [isOpen, api]);

  // Fetch states when region changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedRegion) return;
      try {
        const response = await api.get(`/terminals/states/${encodeURIComponent(selectedRegion)}`);
        setStates(response.data.states);
        setSelectedState('');
        setCities([]);
        setSelectedCities([]);
      } catch (error) {
        console.error('Failed to fetch states:', error);
      }
    };
    fetchStates();
  }, [selectedRegion, api]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedRegion || !selectedState) return;
      try {
        const response = await api.get(`/terminals/cities/${encodeURIComponent(selectedRegion)}/${encodeURIComponent(selectedState)}`);
        setCities(response.data.cities);
        setSelectedCities([]);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCities();
  }, [selectedRegion, selectedState, api]);

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCity = (cityName) => {
    setSelectedCities(prev => 
      prev.includes(cityName) 
        ? prev.filter(c => c !== cityName)
        : [...prev, cityName]
    );
  };

  const handleSubmitCompany = async () => {
    if (!companyData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setLoading(true);
    setLogoGenerating(true);
    
    try {
      const response = await api.post('/company', companyData);
      setGeneratedLogo(response.data.company?.logo_url);
      toast.success('Company created! Logo generated.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setLoading(false);
      setLogoGenerating(false);
    }
  };

  const handleSubmitTerminals = async () => {
    if (selectedCities.length === 0) {
      toast.error('Please select at least one port city');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/terminals/select', {
        region: selectedRegion,
        state: selectedState,
        cities: selectedCities,
        include_rail_ramps: includeRailRamps
      });
      toast.success('Terminals configured!');
      onComplete();
    } catch (error) {
      toast.error('Failed to save terminals');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipTerminals = () => {
    onComplete();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && onClose) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            {step === 1 ? 'Company Setup' : 'Select Working Ports'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 1 
              ? 'Set up your company profile. A logo will be automatically generated.'
              : 'Select the port regions where your company operates.'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-slate-700'}`}>
            {step > 1 ? <Check className="h-4 w-4 text-white" /> : <span className="text-white text-sm">1</span>}
          </div>
          <div className={`flex-1 h-1 ${step > 1 ? 'bg-blue-600' : 'bg-slate-700'}`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-slate-700'}`}>
            <span className="text-white text-sm">2</span>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name *</Label>
              <div className="relative">
                <Truck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={companyData.name}
                  onChange={(e) => handleCompanyChange('name', e.target.value)}
                  placeholder="Your Trucking Company"
                  className="pl-10 bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="text-slate-300">Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={companyData.address}
                  onChange={(e) => handleCompanyChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  className="pl-10 bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-300">City</Label>
                <Input
                  value={companyData.city}
                  onChange={(e) => handleCompanyChange('city', e.target.value)}
                  placeholder="Los Angeles"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">State</Label>
                <Select value={companyData.state} onValueChange={(v) => handleCompanyChange('state', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state} className="text-white">{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">ZIP</Label>
                <Input
                  value={companyData.zip_code}
                  onChange={(e) => handleCompanyChange('zip_code', e.target.value)}
                  placeholder="90001"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-300">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    value={companyData.phone}
                    onChange={(e) => handleCompanyChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="pl-10 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    value={companyData.email}
                    onChange={(e) => handleCompanyChange('email', e.target.value)}
                    placeholder="info@company.com"
                    className="pl-10 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={companyData.website}
                  onChange={(e) => handleCompanyChange('website', e.target.value)}
                  placeholder="www.company.com"
                  className="pl-10 bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            <Button
              onClick={handleSubmitCompany}
              disabled={loading || !companyData.name.trim()}
              className="w-full bg-blue-600 hover:bg-blue-500"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {logoGenerating ? 'Generating Logo...' : 'Creating...'}
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {/* Show generated logo */}
            {generatedLogo && (
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-slate-800 rounded-lg">
                  <img src={generatedLogo} alt="Company Logo" className="h-24 w-24 object-contain" />
                  <p className="text-center text-slate-400 text-xs mt-2">Your generated logo</p>
                </div>
              </div>
            )}

            {/* Region Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Select Region</Label>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                  <SelectValue placeholder="Choose a region" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {regions.map(region => (
                    <SelectItem key={region} value={region} className="text-white">{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* State Selection */}
            {states.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300">Select State</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue placeholder="Choose a state" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {states.map(state => (
                      <SelectItem key={state} value={state} className="text-white">{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* City Selection */}
            {cities.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300">Select Port Cities (multiple allowed)</Label>
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {cities.map(city => (
                    <div 
                      key={city.name} 
                      className="flex items-start gap-3 p-2 hover:bg-slate-900 rounded cursor-pointer"
                      onClick={() => toggleCity(city.name)}
                    >
                      <Checkbox 
                        checked={selectedCities.includes(city.name)}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-white font-medium">{city.name}</p>
                        <p className="text-slate-500 text-xs">{city.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCities.length > 0 && (
              <div className="text-sm text-slate-400">
                Selected: {selectedCities.join(', ')}
              </div>
            )}

            {/* Rail Ramps Option */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="include-rail"
                  checked={includeRailRamps}
                  onCheckedChange={setIncludeRailRamps}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="include-rail" className="text-white font-medium flex items-center gap-2 cursor-pointer">
                    <TrainFront className="h-4 w-4 text-orange-400" />
                    Include Rail Intermodal Ramps
                  </Label>
                  <p className="text-slate-400 text-xs mt-1">
                    Add BNSF, Union Pacific, Norfolk Southern, and CSX rail facilities to your terminal list. 
                    Useful if you handle rail intermodal shipments.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipTerminals}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Skip for Now
              </Button>
              <Button
                onClick={handleSubmitTerminals}
                disabled={loading || selectedCities.length === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-500"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanySetupModal;
